import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import {
	LogIn,
	MenuListItem,
	Profile as ProfileTrigger,
} from '@atlaskit/navigation-system/top-nav-items';
import type { AnyEntryPoint } from '@atlassian/entry-point-types/types';
import { type PopupTriggerProps, PopupTrigger } from '@atlassian/entry-points/popup-trigger';

import { useAnalyticsEvents } from '../../common/analytics/generated/use-analytics-events';
import type { ProfileComponentProps } from '../../common/types';
import type { Profile_AtlassianGlobalTopNavigation$key } from './__generated__/Profile_AtlassianGlobalTopNavigation.graphql';
import { messages } from './messages';

type PopupTriggerRenderProps = Parameters<PopupTriggerProps<AnyEntryPoint>['trigger']>[0];

type ProfileProps<TEntryPoint extends AnyEntryPoint> = ProfileComponentProps<TEntryPoint> & {
	/** Relay reference used to read the current user's avatar and email. */
	queryRef: Profile_AtlassianGlobalTopNavigation$key | null;
};

/**
 * __Profile__
 *
 * The profile item in the top navigation. GTN owns the eager ADS trigger, the
 * identity data, popup lifecycle, entry-point preloading and click analytics.
 * Authenticated apps supply menu content through an entry point; anonymous apps
 * supply the destination for the platform log-in item.
 *
 * The menu is not loaded until the user hovers, focuses or activates the
 * trigger. The trigger itself is always rendered for an authenticated user.
 */
export function Profile<TEntryPoint extends AnyEntryPoint>(
	props: ProfileProps<TEntryPoint>,
): JSX.Element | null {
	const { interactionName, onClick, queryRef, testId } = props;
	const { formatMessage } = useIntl();
	const { fireEvent } = useAnalyticsEvents();
	const profileData = useFragment<Profile_AtlassianGlobalTopNavigation$key>(
		graphql`
			fragment Profile_AtlassianGlobalTopNavigation on Query {
				me {
					user {
						picture
						... on AtlassianAccountUser {
							email
						}
					}
				}
			}
		`,
		queryRef,
	);
	const profileUser = profileData?.me?.user;
	const resolvedLabel = profileUser?.email ?? formatMessage(messages.account);

	const renderTrigger = useCallback(
		(triggerProps: PopupTriggerRenderProps) => (
			<ProfileTrigger
				{...triggerProps}
				interactionName={interactionName}
				isListItem={false}
				isSelected={triggerProps['aria-expanded']}
				label={resolvedLabel}
				onClick={(event) => {
					fireEvent('ui.topNav.clicked.profile', {});
					onClick?.(event);
				}}
				src={profileUser?.picture}
				testId={testId}
			/>
		),
		[fireEvent, interactionName, onClick, profileUser?.picture, resolvedLabel, testId],
	);

	if (props.isAnonymous) {
		if (!props.logInLink) {
			return null;
		}

		return (
			<LogIn
				href={props.logInLink}
				interactionName={interactionName}
				label={formatMessage(messages.logIn)}
				onClick={() => {
					fireEvent('ui.topNav.clicked.login', {});
				}}
				testId={testId}
			/>
		);
	}

	return (
		<MenuListItem>
			<PopupTrigger
				placement="bottom"
				entryPoint={props.entryPoint}
				entryPointParams={props.entryPointParams}
				entryPointProps={props.entryPointProps}
				errorFallback={props.errorFallback}
				fallback={props.fallback}
				shouldRenderToParent
				testId={props.popupTestId}
				trigger={renderTrigger}
			/>
		</MenuListItem>
	);
}
